import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMode } from '@/providers/ModeProvider';
import { useRegisterPageRefresh } from '@/contexts/PageRefreshContext';
import { dataService } from '@/services/data-service';
import type { KPI, Asset, Transaction } from '@/types/asset';
import { DepreciationYTDCard } from '@/components/dashboard/depreciation-ytd-card';
import { AssetsByStatusCard } from '@/components/dashboard/assets-by-status-card';
import { AssetsByLocationCard } from '@/components/dashboard/assets-by-location-card';
import { AssetsByCategoryCard } from '@/components/dashboard/assets-by-category-card';
import { AssetsDueForReviewCard } from '@/components/dashboard/assets-due-for-review-card';
import { NetBookValueCard } from '@/components/dashboard/net-book-value-card';
import { UpcomingInsuranceRenewalsCard } from '@/components/dashboard/upcoming-insurance-renewals-card';
import { RecentActivityCard } from '@/components/dashboard/recent-activity-card';
import { ComplianceSummaryWidget } from '@/components/dashboard/widgets/compliance-summary-widget';
import { UpcomingInspectionsWidget } from '@/components/dashboard/widgets/upcoming-inspections-widget';
import { OverdueInspectionsWidget } from '@/components/dashboard/widgets/overdue-inspections-widget';
import { EvoFeatureFeedbackCollector } from '@/components/FeedbackCollector';
import {
  Home,
  TrendingUp,
  ArrowDownCircle,
  PlusCircle,
  Shield,
  AlertCircle,
  ArrowUp,
  ArrowDown,
  Minus,
} from 'lucide-react';

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Home,
  TrendingUp,
  ArrowDownCircle,
  PlusCircle,
  Shield,
  AlertCircle,
};

export function Dashboard(): React.ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const { mode } = useMode();
  const [kpis, setKpis] = useState<KPI[]>([]);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [assetsDueForReview, setAssetsDueForReview] = useState<{
    totalCount: number;
    depreciationCount: number;
    insuranceCount: number;
    warrantyCount: number;
  }>({ totalCount: 0, depreciationCount: 0, insuranceCount: 0, warrantyCount: 0 });
  const [insuranceRenewals, setInsuranceRenewals] = useState<{
    assetId: string;
    assetCode: string;
    assetName: string;
    renewalDate: string;
    insuranceProvider?: string;
    insurancePolicyNumber?: string;
    insuranceValue?: number;
  }[]>([]);
  const [netBookValueTrend, setNetBookValueTrend] = useState<{
    currentValue: number;
    change: number;
    trend: string;
    monthlyData: { month: string; value: number }[];
  }>({ currentValue: 0, change: 0, trend: 'neutral', monthlyData: [] });
  const [depreciationYTDTrend, setDepreciationYTDTrend] = useState<{
    currentValue: number;
    change: number;
    trend: string;
    monthlyData: { month: string; value: number }[];
  }>({ currentValue: 0, change: 0, trend: 'neutral', monthlyData: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isActivityPanelCollapsed, setIsActivityPanelCollapsed] = useState(false);

  const loadDashboardData = useCallback(async () => {
      try {
        setLoading(true);
        console.log('🚀 Dashboard loading - Current mode:', mode);
        
        // Clear data to prevent showing stale data while loading
        setAssets([]);
        setTransactions([]);
        setKpis([]);
        
        let kpisData: KPI[];
        let assetsData: { data: Asset[]; total: number; page: number; pageSize: number; totalPages: number } | Asset[];
        let transactionsData: { data: Transaction[]; total: number; page: number; pageSize: number; totalPages: number } | Transaction[];
        let assetsDueForReviewData: { totalCount: number; depreciationCount: number; insuranceCount: number; warrantyCount: number };
        let insuranceRenewalsData: { assetId: string; assetCode: string; assetName: string; renewalDate: string; insuranceProvider?: string; insurancePolicyNumber?: string; insuranceValue?: number; }[];
        let nbvTrendData: { currentValue: number; change: number; trend: 'neutral'; monthlyData: { month: string; value: number }[] };
        let depreciationTrendData: { currentValue: number; change: number; trend: 'neutral'; monthlyData: { month: string; value: number }[] };
        
        if (mode === 'demo') {
          // In demo mode, use demo data directly
          [kpisData, assetsData, transactionsData, assetsDueForReviewData, insuranceRenewalsData, nbvTrendData, depreciationTrendData] = await Promise.all([
            Promise.resolve(dataService.getDemoDashboardKPIs()),
            Promise.resolve(dataService.getDemoAssets({ pageSize: 10000 })),
            Promise.resolve(dataService.getDemoTransactions({ pageSize: 10000 })),
            dataService.getAssetsDueForReview(),
            dataService.getUpcomingInsuranceRenewals(90),
            dataService.getNetBookValueTrend(),
            dataService.getDepreciationYTDTrend(),
          ]);
        } else {
          // In live mode, call API only - fall back to demo on error for each call
          [kpisData, assetsData, transactionsData, assetsDueForReviewData, insuranceRenewalsData, nbvTrendData, depreciationTrendData] = await Promise.all([
            dataService.getDashboardKPIsApiOnly().catch((err: Error) => {
              console.warn('KPIs API failed, falling back to demo:', err.message);
              return dataService.getDemoDashboardKPIs();
            }),
            dataService.getAssetsApiOnly({ pageSize: 10000 }).catch((err: Error) => {
              console.warn('Assets API failed, falling back to demo:', err.message);
              return dataService.getDemoAssets({ pageSize: 10000 });
            }),
            dataService.getTransactionsApiOnly({ pageSize: 10000 }).catch((err: Error) => {
              console.warn('Transactions API failed, falling back to demo:', err.message);
              return dataService.getDemoTransactions({ pageSize: 10000 });
            }),
            dataService.getAssetsDueForReview().catch((err: Error) => {
              console.warn('Assets Due for Review API failed:', err.message);
              return { totalCount: 0, depreciationCount: 0, insuranceCount: 0, warrantyCount: 0 };
            }),
            dataService.getUpcomingInsuranceRenewals(90).catch((err: Error) => {
              console.warn('Insurance Renewals API failed:', err.message);
              return [];
            }),
            dataService.getNetBookValueTrend().catch((err: Error) => {
              console.warn('NBV Trend API failed:', err.message);
              return { currentValue: 0, change: 0, trend: 'neutral', monthlyData: [] };
            }),
            dataService.getDepreciationYTDTrend().catch((err: Error) => {
              console.warn('Depreciation YTD Trend API failed:', err.message);
              return { currentValue: 0, change: 0, trend: 'neutral', monthlyData: [] };
            }),
          ]);
        }
        
        console.log('📦 Raw data received:', {
          kpisData,
          assetsData,
          transactionsData
        });
        
        let assetsArray = Array.isArray(assetsData) ? assetsData : assetsData.data ?? [];
        let transactionsArray = Array.isArray(transactionsData) ? transactionsData : transactionsData.data ?? [];
        
        console.log('🔍 Parsed arrays:', {
          mode,
          assetsArrayLength: assetsArray.length,
          transactionsArrayLength: transactionsArray.length,
          assetsArrayType: Array.isArray(assetsArray),
          transactionsArrayType: Array.isArray(transactionsArray)
        });
        
        // Log which mode we're in - fallback to demo data is now handled by DataService on API errors only
        if (mode === 'live') {
          console.log('🟢 LIVE MODE - Using API data (fallback to demo on errors handled by DataService)');
        } else {
          console.log('🟢 DEMO MODE - Using data from data-service');
        }
        
        console.log('Dashboard Data Loaded:', {
          mode,
          kpisData,
          assetsCount: assetsArray.length,
          transactionsCount: transactionsArray.length
        });
        
        console.log('🎯 About to set transactions state:', {
          transactionsArrayLength: transactionsArray.length,
          firstTransaction: transactionsArray[0],
          allTransactions: transactionsArray
        });
        
        setAssets(assetsArray);
        setTransactions(transactionsArray);
        setAssetsDueForReview(assetsDueForReviewData);
        setInsuranceRenewals(insuranceRenewalsData);
        setNetBookValueTrend(nbvTrendData);
        setDepreciationYTDTrend(depreciationTrendData);
        
        console.log('✅ Transactions state has been set');
        console.log('✅ Dashboard widget data loaded:', {
          assetsDueForReview: assetsDueForReviewData,
          insuranceRenewals: insuranceRenewalsData.length,
          nbvTrend: nbvTrendData,
          depreciationTrend: depreciationTrendData
        });
        
        // Calculate KPIs from assets data if API doesn't provide valid KPIs
        let calculatedKpis = kpisData;
        
        // Check if KPIs have valid labels
        const hasValidKpis = kpisData && kpisData.length > 0 && kpisData.some((kpi: any) => kpi.label && kpi.label.trim() !== '');
        
        if (!hasValidKpis) {
          console.log('Calculating KPIs from assets data...');
          // Calculate Total Asset Value
          const totalAssetValue = assetsArray.reduce((sum: any, asset: any) => sum + (asset.netBookValue ?? 0), 0);
          
          // Count Active Assets
          const activeAssets = assetsArray.filter((asset: any) => asset.status === 'inService').length;
          
          console.log('Calculated KPIs:', { totalAssetValue, activeAssets });
          
          calculatedKpis = [
            {
              label: 'Total Asset Value',
              value: totalAssetValue,
              change: 5.2,
              trend: 'up' as const,
              icon: 'Home',
            },
            {
              label: 'Active Assets',
              value: activeAssets,
              change: 2.1,
              trend: 'up' as const,
              icon: 'TrendingUp',
            },
          ];
        }
        
        console.log('Final KPIs:', calculatedKpis);
        setKpis(calculatedKpis);
        setError(null);
      } catch (err) {
        console.error('Failed to load dashboard data:', err);
        setError('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
  }, [mode]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  // Register refresh handler for breadcrumb
  useRegisterPageRefresh(loadDashboardData, location.pathname);


  if (loading) {
    return (
      <div className="space-y-6 p-6">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {['kpi-1', 'kpi-2', 'kpi-3', 'kpi-4'].map((id) => (
            <Card key={id}>
              <CardHeader>
                <Skeleton className="h-4 w-24" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-32" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6 p-6">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center text-red-500">
              <AlertCircle className="mx-auto h-12 w-12 mb-4" />
              <p>{error}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Calculate Assets by Location dynamically from API data
  const calculateAssetsByLocation = () => {
    // Define pastel colors for each location (complementary to green primary)
    const locationColors: Record<string, string> = {
      'London HQ': '#B4A7D6',                    // Soft Lavender
      'Manchester Office': '#A8C5E8',            // Soft Periwinkle
      'Birmingham Warehouse': '#D4B5E8',         // Soft Lilac
      'Edinburgh Branch': '#9DB8D9',             // Soft Sky Blue
      'Leeds Distribution Center': '#C5B8E0',    // Soft Mauve
      'Bristol Office': '#B8C9E8',               // Soft Powder Blue
    };

    // Initialize location totals and counts dynamically from assets
    const locationData: Record<string, { value: number; count: number }> = {};

    // Sum up netBookValue and count for each location
    assets.forEach(asset => {
      if (!locationData[asset.location]) {
        locationData[asset.location] = { value: 0, count: 0 };
      }
      locationData[asset.location].value += asset.netBookValue;
      locationData[asset.location].count += 1;
    });

    // Convert to array format for the card, sorted by value descending
    return Object.entries(locationData)
      .map(([location, data]) => ({
        location,
        value: data.value,
        count: data.count,
        color: locationColors[location] || '#6b7280', // Default gray for unmapped locations
      }))
      .sort((a: any, b: any) => b.value - a.value); // Sort by value descending
  };

  // Calculate Assets by Category dynamically from API data
  const calculateAssetsByCategory = () => {
    // Define pastel colors for each category (complementary to green primary)
    const categoryColors: Record<string, string> = {
      'Computer Equipment': '#F5B8B8',    // Soft Coral
      'Furniture & Fixtures': '#F5D4B8',  // Soft Peach
      'Vehicles': '#F5E5B8',              // Soft Cream
      'Machinery': '#F5C8D4',             // Soft Rose
      'Buildings': '#F5D0C8',             // Soft Apricot
    };

    // Initialize category totals and counts dynamically from assets
    const categoryData: Record<string, { value: number; count: number }> = {};

    // Sum up netBookValue and count for each category
    assets.forEach(asset => {
      if (!categoryData[asset.category]) {
        categoryData[asset.category] = { value: 0, count: 0 };
      }
      categoryData[asset.category].value += asset.netBookValue;
      categoryData[asset.category].count += 1;
    });

    // Convert to array format for the bar chart, sorted by value descending
    return Object.entries(categoryData)
      .map(([category, data]) => ({
        category,
        value: data.value,
        count: data.count,
        color: categoryColors[category] || '#6b7280', // Default gray for unmapped categories
      }))
      .sort((a: any, b: any) => b.value - a.value); // Sort by value descending
  };

  // Calculate Assets by Status dynamically from API data
  const calculateAssetsByStatus = () => {
    // Define subtle pastel colors for each status (soft greens/teals - distinct from Location blues and Category warm tones)
    const statusColors: Record<string, string> = {
      'inService': '#A8D5BA',        // Soft Sage Green
      'outOfService': '#D4E5ED',     // Soft Ice Blue
      'disposed': '#D9E4DD',         // Soft Eucalyptus
      'fullyDepreciated': '#E8D5E8', // Soft Lavender
      'pending': '#B8E0D2',          // Soft Mint
      'underMaintenance': '#C5DDD8', // Soft Seafoam
      'reserved': '#D5E0F0',         // Soft Periwinkle
      'lostMissing': '#F0D5D5',      // Soft Rose
      'leased': '#D5F0E8',           // Soft Aqua
    };

    // Define display names for statuses
    const statusNames: Record<string, string> = {
      'inService': 'In Service',
      'outOfService': 'Out of Service',
      'disposed': 'Disposed',
      'fullyDepreciated': 'Fully Depreciated',
      'pending': 'Pending / On Order',
      'underMaintenance': 'Under Maintenance',
      'reserved': 'Reserved',
      'lostMissing': 'Lost / Missing',
      'leased': 'Leased',
    };

    // Initialize status counts and values
    const statusData: Record<string, { count: number; value: number }> = {
      'inService': { count: 0, value: 0 },
      'outOfService': { count: 0, value: 0 },
      'disposed': { count: 0, value: 0 },
      'fullyDepreciated': { count: 0, value: 0 },
      'pending': { count: 0, value: 0 },
      'underMaintenance': { count: 0, value: 0 },
      'reserved': { count: 0, value: 0 },
      'lostMissing': { count: 0, value: 0 },
      'leased': { count: 0, value: 0 },
    };

    // Count assets and sum values by status
    assets.forEach(asset => {
      const status = asset.status.toLowerCase();
      if (statusData.hasOwnProperty(status)) {
        statusData[status].count++;
        statusData[status].value += asset.netBookValue;
      }
    });

    // Convert to array format for the pie chart
    return Object.entries(statusData)
      .map(([status, data]) => ({
        name: statusNames[status] || status,
        value: data.value,
        count: data.count,
        fill: statusColors[status] || '#6b7280',
        status: status, // Keep original status for navigation
      }))
      .filter(item => item.count > 0); // Only include statuses with assets
  };

  // Calculate dynamic chart data from API assets
  const dynamicChartData = {
    assetsByStatus: calculateAssetsByStatus(),
    assetsByLocation: calculateAssetsByLocation(),
    assetsByCategory: calculateAssetsByCategory(),
  };

    return (
      <div className="flex h-full flex-col">
        {/* Content */}
        <div className="flex flex-1 overflow-hidden">
          {/* Main Content Area */}
          <div className="flex-1 space-y-6 overflow-y-auto p-6">
            {/* Feedback Collector */}
            <EvoFeatureFeedbackCollector feature="Dashboard" />
            {/* KPI Cards - All on one line */}
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2 xl:grid-cols-4">
          {kpis.filter(kpi => kpi.label && !kpi.label.includes('Depreciation') && !kpi.label.includes('Assets Due for Review')).map((kpi) => {
          const Icon = iconMap[kpi.icon ?? 'Home'];
          
          // Format value based on KPI type
          const formatValue = (value: string | number, label: string): string => {
            if (typeof value === 'number') {
              // Financial format for Total Asset Value and Depreciation
              if (label.includes('Total Asset Value') || label.includes('Depreciation')) {
                return `£${value.toLocaleString('en-GB')}`;
              }
              return value.toLocaleString('en-GB');
            }
            return value;
          };
          
          const isTotalAssetValue = kpi.label.includes('Total Asset Value');
          const isActiveAssets = kpi.label.includes('Active Assets');
          const isClickable = isTotalAssetValue || isActiveAssets;
          
          const handleClick = () => {
            if (isTotalAssetValue) {
              navigate('/assets');
            } else if (isActiveAssets) {
              navigate('/assets?status=inService');
            }
          };
          
          // Define colors for each card type
          const getCardColors = () => {
            if (isTotalAssetValue) {
              return {
                borderColor: 'border-l-[oklch(0.5_0.09_194.77)]',
                bgColor: 'bg-[oklch(0.5_0.09_194.77)]/5',
                iconBgColor: 'bg-[oklch(0.5_0.09_194.77)]/10',
                iconColor: 'text-[oklch(0.5_0.09_194.77)]',
                textColor: 'text-[oklch(0.5_0.09_194.77)]',
              };
            } else if (isActiveAssets) {
              return {
                borderColor: 'border-l-green-500',
                bgColor: 'bg-green-500/5',
                iconBgColor: 'bg-green-50 dark:bg-green-950',
                iconColor: 'text-green-600 dark:text-green-400',
                textColor: 'text-green-600 dark:text-green-400',
              };
            }
            return {
              borderColor: 'border-l-blue-500',
              bgColor: 'bg-blue-500/5',
              iconBgColor: 'bg-blue-50 dark:bg-blue-950',
              iconColor: 'text-blue-600 dark:text-blue-400',
              textColor: 'text-blue-600 dark:text-blue-400',
            };
          };
          
          const colors = getCardColors();
          
          return (
            <Card 
              key={kpi.label}
              className={`relative overflow-hidden border shadow-sm hover:shadow-md transition-all duration-300 bg-white dark:bg-black ${isClickable ? "cursor-pointer" : ""}`}
              onClick={isClickable ? handleClick : undefined}
            >
              <div className={`absolute top-0 right-0 w-32 h-32 ${colors.bgColor} rounded-full -mr-16 -mt-16`} />
              <CardContent className="relative p-6">
                <div className="flex items-center gap-2 mb-3">
                  <div className={`p-2 rounded-full ${colors.iconBgColor}`}>
                    <Icon className={`h-4 w-4 ${colors.iconColor}`} />
                  </div>
                  <p className="text-sm font-medium text-muted-foreground">{kpi.label}</p>
                </div>
                <h3 className={`text-3xl font-bold ${colors.textColor} mb-2`}>{formatValue(kpi.value, kpi.label)}</h3>
                {kpi.change !== undefined && kpi.change !== 0 && (
                  <p className="text-sm text-muted-foreground flex items-center gap-1">
                    {kpi.trend === 'up' && (
                      <>
                        <ArrowUp className="h-3 w-3 text-green-500" />
                        <span className="text-green-500">
                          +{kpi.change}%
                        </span>
                      </>
                    )}
                    {kpi.trend === 'down' && (
                      <>
                        <ArrowDown className="h-3 w-3 text-red-500" />
                        <span className="text-red-500">
                          {kpi.change}%
                        </span>
                      </>
                    )}
                    {kpi.trend === 'neutral' && (
                      <>
                        <Minus className="h-3 w-3" />
                        <span>No change</span>
                      </>
                    )}
                    <span className="ml-1">from last month</span>
                  </p>
                )}
              </CardContent>
            </Card>
          );
          })}
          
          {/* Assets Due for Review - Custom Card with Pills - Spans 2 columns */}
          <div className="md:col-span-2 xl:col-span-2">
            <AssetsDueForReviewCard
              totalCount={assetsDueForReview.totalCount}
              depreciationCount={assetsDueForReview.depreciationCount}
              insuranceCount={assetsDueForReview.insuranceCount}
              warrantyCount={assetsDueForReview.warrantyCount}
            />
          </div>
        </div>

        {/* Compliance Widgets Row */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <ComplianceSummaryWidget />
          <UpcomingInspectionsWidget />
          <OverdueInspectionsWidget />
        </div>

        {/* Featured Cards Row */}
        <div className="grid gap-4 md:grid-cols-2">
          {/* Net Book Value Trend Card */}
          <div>
            <NetBookValueCard
              currentValue={netBookValueTrend.currentValue}
              monthlyData={netBookValueTrend.monthlyData}
              assets={assets}
            />
          </div>

          {/* Depreciation YTD Card */}
          <DepreciationYTDCard
            currentValue={depreciationYTDTrend.currentValue}
            monthlyData={depreciationYTDTrend.monthlyData}
            assets={assets}
            transactions={transactions}
          />
        </div>

        {/* Charts Row */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {/* Assets by Status */}
          <div>
            <AssetsByStatusCard data={dynamicChartData.assetsByStatus} />
          </div>

          {/* Assets by Location */}
          <div>
            <AssetsByLocationCard data={dynamicChartData.assetsByLocation} />
          </div>

          {/* Assets by Category */}
          <div>
            <AssetsByCategoryCard data={dynamicChartData.assetsByCategory} />
          </div>
        </div>

          {/* Upcoming Renewals - Full Width */}
          <UpcomingInsuranceRenewalsCard renewals={insuranceRenewals} />
        </div>

        {/* Right Panel - Recent Activity (Full Height) - Hidden on smaller screens */}
        <div className={`hidden xl:block flex-shrink-0 h-full transition-all duration-300 ${isActivityPanelCollapsed ? 'w-16' : 'w-[350px]'}`}>
          <RecentActivityCard 
            transactions={transactions}
            isCollapsed={isActivityPanelCollapsed}
            onToggleCollapse={() => setIsActivityPanelCollapsed(!isActivityPanelCollapsed)}
          />
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
